<template>
  <owal-modal
    :show="show"
    class="owal-clip-picker-modal__container"
  >
    <div
      slot="content"
      class="owal-clip-picker-modal__panel"
    >
      Please select a time range for your video download.

      <div class="owal-clip-picker-modal__content">
        Start Time:
        <div class='owal-clip-picker__datetime-row'>
          <owal-date-time-input
            :changeFunction="computeNewDatetime"
            :value="startDate"
            type="date"
            id="start-date"
            />
          <owal-date-time-input
            :changeFunction="computeNewDatetime"
            :value="startTime"
            type="time"
            id="start-time"
            />
        </div>
      </div>

      <div class="owal-clip-picker-modal__content">
        End Time:
        <div class='owal-clip-picker__datetime-row'>
          <owal-date-time-input
            :changeFunction="computeNewDatetime"
            :value="endDate"
            type="date"
            id="end-date"
            />
          <owal-date-time-input
            :changeFunction="computeNewDatetime"
            :value="endTime"
            type="time"
            id="end-time"
            />
        </div>
      </div>
      <div style="display: flex; flex-direction: row; justify-content: space-around; align-content: center;">
        <owal-button @click='checkForError()'>Download</owal-button>
        <owal-button @click='close()'>Cancel</owal-button>
      </div>
    </div>
  </owal-modal>
</template>
<script>
import OwalButton from '@/libraries/owal-ui/buttons/OwalButton'
import OwalDateTimeInput from '@/libraries/owal-ui/inputs/OwalDateTimeInput.vue'
import OwalModal from '@/libraries/owal-ui/modals/OwalModal'
import { getLongClip } from '@/libraries/api/longClip'
import { mapMutations } from 'vuex'
import moment from 'moment'

export default {
  name: 'OwalLongClipRequest',
  components: { OwalModal, OwalButton, OwalDateTimeInput },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    cameraId: {
      type: String,
    },
  },
  data () {
    return {
      searchTime: {
        start: undefined,
        end: undefined,
      },
      datetimeFormat: {
        time: 'HH:mm:ss',
        date: 'YYYY-MM-DD',
      },
    }
  },
  computed: {
    maxDate () {
      return moment().format('YYYY-MM-DD')
    },
    endDate () {
      const timestamp = this.searchTime.end ? moment.utc(this.searchTime.end) : moment.utc()
      const dateString = timestamp.local().format(this.datetimeFormat.date)

      return dateString
    },
    endTime () {
      const timestamp = this.searchTime.end ? moment.utc(this.searchTime.end) : moment.utc()
      const dateString = timestamp.local().format(this.datetimeFormat.time)
      return dateString
    },
    startDate () {
      if (!this.searchTime.start) {
        return undefined
      }
      const timestamp = this.searchTime.start ? moment.utc(this.searchTime.start) : undefined
      const dateString = timestamp.local().format(this.datetimeFormat.date)

      return dateString
    },
    startTime () {
      if (!this.searchTime.start) {
        return undefined
      } else {
        const timestamp = this.searchTime.start ? moment.utc(this.searchTime.start) : undefined
        const dateString = timestamp.local().format(this.datetimeFormat.time)
        return dateString
      }
    },
  },
  methods: {
    ...mapMutations({
      setDownloadingClip: 'viewer/setDownloadingClip',
    }),
    computeNewDatetime: function (target) {
      /**
       * Note: this functionality should be split out since it's also being used in OwalFilterPanel.vue
       * Two options: A) refactor function to accept start and end targets B) new component with a date and time picker + function
       */
      let timestamp

      const id = target.id.replace(/-\w*/, '')
      /**
       * incoming value is local time (i.e. moment() interprets as local, moment.utc() interprets as utc)
       * https://stackoverflow.com/questions/50608132/difference-between-moment-utcdate-and-momentdate-utc
       */
      const incoming = moment(target.value, this.datetimeFormat[target.type])
      const localcurrentDatetime = this.searchTime[id] ? moment.utc(this.searchTime[id]).local() : moment()

      // convert incoming date to utc and use string template to update store
      if (target.type === 'date' && incoming.isValid()) {
        timestamp = moment(`${incoming.format(this.datetimeFormat.date)} ${localcurrentDatetime.format(this.datetimeFormat.time)}`, `${this.datetimeFormat.date} ${this.datetimeFormat.time}`)
      } else if (target.type === 'time' && incoming.isValid()) {
        timestamp = moment(`${localcurrentDatetime.format(this.datetimeFormat.date)} ${incoming.format(this.datetimeFormat.time)}`, `${this.datetimeFormat.date} ${this.datetimeFormat.time}`)
      } else {
        console.debug('Unknown date or time input')
      }
      this.searchTime[id] = timestamp.utc().format()
    },
    close () {
      this.$emit('close')
    },
    checkForError () {
      const start = moment(this.searchTime.start).toISOString()
      const end = moment(this.searchTime.end).toISOString()
      let configurationError = false

      const now = moment().toISOString()
      // 1. check if start and end time is not in the future
      // 2. check if start time is indeed before the end time for a logical time range.
      // 3. check if duration is more than 2 hours. Limit is 2 hour clip.
      if (moment(start).isAfter(now) || moment(end).isAfter(now)) {
        configurationError = true
        alert('Unfortunately we cannot predict the future!')
      } else if (moment(start).isAfter(end)) {
        configurationError = true
        alert('The date and time you selected is logically incorrect.')
      } else if (moment.duration(moment(end).diff(moment(start))).as('hours') > 2) {
        configurationError = true
        alert('Please select a smaller time range')
      } else {
        this.requestClip(start, end)
      }
      if (!configurationError) { this.close() }
    },
    requestClip (start, end) {
      this.setDownloadingClip(true)
      getLongClip(this.cameraId, start, end).then(res => {
        if (res.data) {
          const url = res.data.data.url
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', 'longClip.mp4')
          document.body.appendChild(link)
          link.click()
        }
      }).catch(error => {
        alert(`Error! ${error}`)
      }).then(function () {
        this.setDownloadingClip(false)
      }.bind(this))
    },
  },
}
</script>
<style scoped>
.owal-clip-picker-modal__container {
  display: flex;
  height: 100%;
  width: 100%;
  overflow: auto;
  padding: 5rem;
}
.owal-clip-picker-modal__panel {
  display: flex;
  flex-direction: column;
  align-self: start;
  height: 100%;

  padding: 15px;
  background-color: #F3F4F5;
  box-shadow: 0 0 5px #555;
  border-radius: 5px;
  text-align: center;
  font-weight: bold;
}
.owal-clip-picker__datetime-row {
  display: flex;
  flex-direction: row;
}
.owal-clip-picker-modal__content {
  text-align: left;
  text-decoration: underline;
}
.owal-clip-picker-modal__picker {
  width: 100%;
  margin: 5px;
}
.owal-clip-picker-modal__button {
  display: flex;
  background-color: white;
  border: 1px solid transparent;
  border-radius: 4px;
  font-size: 13px;
  outline: none;
  cursor: pointer;
  -webkit-transition: all 0.25s cubic-bezier(0.645, 0.045, 0.355, 1);
  color: black;
  font-weight: 500;
  margin: 5px 0px;
  width: 25%;
  height: 30px;
  align-self: center;
  align-content: center;
  justify-content: center;
}

.owal-clip-picker-modal__button:hover {
  background: #5b7ea0;
}

</style>
